import React, { useState, useEffect, useRef } from 'react';
import {
  MapPin, Clock, Navigation, Search, Filter,
  Info, ChevronRight, Bus, Calendar
} from 'lucide-react';
import { routesAPI } from '../services/api';
import './Routes.css';

const Routes = () => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState('tur');
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);

  useEffect(() => {
    loadRoutes();
  }, []);

  useEffect(() => {
    if (selectedRoute && selectedRoute.variante && window.H) {
      initializeMap();
    }
  }, [selectedRoute, selectedDirection]);

  const initializeMap = async () => {
    const varianta = selectedRoute?.variante?.find(v => v.directie === selectedDirection) || selectedRoute?.variante?.[0];
    const statii = varianta?.statii || [];

    if (!mapRef.current || statii.length === 0) {
      return;
    }

    // Clear existing map if any
    if (googleMapRef.current) {
      googleMapRef.current.dispose();
      googleMapRef.current = null;
    }

    // Clear map container
    mapRef.current.innerHTML = '';

    // HERE Maps API Key
    const apiKey = '7tPQ1cnKYxBN02xENx9VvfkQqg7aTJwGvffX0erv07M';

    // Initialize HERE platform
    const platform = new window.H.service.Platform({
      apikey: apiKey
    });

    const defaultLayers = platform.createDefaultLayers();

    // Initialize map
    const map = new window.H.Map(
      mapRef.current,
      defaultLayers.vector.normal.map,
      {
        zoom: 10,
        center: { lat: 47.65, lng: 26.30 }
      }
    );

    googleMapRef.current = map;

    // Add map events (pan only, no zoom)
    const mapEvents = new window.H.mapevents.MapEvents(map);
    new window.H.mapevents.Behavior(mapEvents);


    // Create UI without default controls
    const ui = new window.H.ui.UI(map);

    // Group to hold all map objects
    const group = new window.H.map.Group();
    map.addObject(group);

    let bounds = new window.H.geo.Rect(90, -180, -90, 180);
    let first = true;

    console.log('Creating markers for', statii.length, 'stations');

    // Add markers for all stations
    statii.forEach((statie, index) => {
      if (statie.coordonate) {
        const isFirst = index === 0;
        const isLast = index === statii.length - 1;

        // Determine marker style
        const color = isFirst ? '#10b981' : isLast ? '#EF4444' : '#0090C9';
        const size = isFirst || isLast ? 16 : 12;
        const shadowColor = isFirst ? 'rgba(16, 185, 129, 0.4)' : isLast ? 'rgba(239, 68, 68, 0.4)' : 'rgba(0, 144, 201, 0.4)';

        // Create beautiful SVG marker with shadow and gradient
        const svgCircle = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="shadow${index}" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="0" dy="2" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <radialGradient id="grad${index}">
              <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${color};stop-opacity:0.8" />
            </radialGradient>
          </defs>
          <circle cx="20" cy="20" r="${size + 4}" fill="${shadowColor}" opacity="0.5"/>
          <circle cx="20" cy="20" r="${size}" fill="url(#grad${index})" stroke="white" stroke-width="3" filter="url(#shadow${index})"/>
          <text x="20" y="${isFirst || isLast ? 25 : 24}" font-size="${isFirst || isLast ? 13 : 11}" font-weight="bold" fill="white" text-anchor="middle" font-family="Arial, sans-serif">${index + 1}</text>
        </svg>`;

        // Create icon from SVG
        const icon = new window.H.map.Icon(svgCircle);

        // Create marker
        const marker = new window.H.map.Marker(
          { lat: statie.coordonate.lat, lng: statie.coordonate.lng },
          { icon: icon, volatility: true }
        );

        // Add info bubble on click
        marker.addEventListener('tap', (evt) => {
          const bubble = new window.H.ui.InfoBubble(evt.target.getGeometry(), {
            content: `
              <div style="padding: 12px; min-width: 200px;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                  <div style="width: 24px; height: 24px; border-radius: 50%; background: ${isFirst ? '#10b981' : isLast ? '#EF4444' : '#0090C9'}; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px;">
                    ${index + 1}
                  </div>
                  <h3 style="margin: 0; font-size: 15px; font-weight: 600; color: #1f2937;">${statie.nume}</h3>
                </div>
                <div style="font-size: 13px; color: #6b7280; line-height: 1.6;">
                  ${isFirst ? '<div style="color: #10b981; font-weight: 600; margin-bottom: 4px;">📍 Stație de plecare</div>' : ''}
                  ${isLast ? '<div style="color: #EF4444; font-weight: 600; margin-bottom: 4px;">🎯 Stație finală</div>' : ''}
                  ${statie.km > 0 ? `<div>📏 Distanță: <strong>${statie.km.toFixed(1)} km</strong></div>` : ''}
                  ${statie.pret > 0 ? `<div>💰 Preț: <strong>${statie.pret.toFixed(2)} RON</strong></div>` : ''}
                </div>
              </div>
            `
          });
          ui.addBubble(bubble);
        });

        // Add marker directly to map
        map.addObject(marker);
        console.log('Added marker', index + 1, 'for station:', statie.nume);

        // Update bounds
        if (first) {
          bounds = new window.H.geo.Rect(
            statie.coordonate.lat,
            statie.coordonate.lng,
            statie.coordonate.lat,
            statie.coordonate.lng
          );
          first = false;
        } else {
          bounds = bounds.mergeLatLng(statie.coordonate.lat, statie.coordonate.lng);
        }
      }
    });

    // Calculate and draw route using HERE Routing API v8
    if (statii.length > 1) {
      try {
        const statiiWithCoords = statii.filter(s => s.coordonate);

        if (statiiWithCoords.length < 2) {
          console.warn('Not enough stations with coordinates');
          return;
        }

        // Construct routing request URL with origin and destination
        const origin = statiiWithCoords[0].coordonate;
        const destination = statiiWithCoords[statiiWithCoords.length - 1].coordonate;

        let routingUrl = `https://router.hereapi.com/v8/routes?transportMode=car&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}&return=polyline&apiKey=${apiKey}`;

        // Add intermediate waypoints (via points)
        for (let i = 1; i < statiiWithCoords.length - 1; i++) {
          const coord = statiiWithCoords[i].coordonate;
          routingUrl += `&via=${coord.lat},${coord.lng}`;
        }

        console.log('Routing URL:', routingUrl);

        // Fetch route from HERE Routing API
        const response = await fetch(routingUrl);
        const routingData = await response.json();

        console.log('Routing response:', routingData);

        if (routingData.routes && routingData.routes.length > 0) {
          const route = routingData.routes[0];

          // Process all sections of the route
          route.sections.forEach(section => {
            if (section.polyline) {
              // Decode the flexible polyline
              const lineString = window.H.geo.LineString.fromFlexiblePolyline(section.polyline);

              // Create polyline from the route section
              const polyline = new window.H.map.Polyline(lineString, {
                style: {
                  strokeColor: selectedRoute.color || '#004A7C',
                  lineWidth: 5,
                  lineCap: 'round',
                  lineJoin: 'round'
                }
              });

              group.addObject(polyline);
            }
          });

          console.log('Route drawn successfully with', route.sections.length, 'sections');
        } else {
          console.warn('No routes found in response, falling back to simple polyline');
          throw new Error('No routes found');
        }
      } catch (error) {
        console.error('Error fetching route:', error);

        // Fallback to simple polyline if routing fails
        const lineString = new window.H.geo.LineString();
        statii.forEach(s => {
          if (s.coordonate) {
            lineString.pushLatLngAlt(s.coordonate.lat, s.coordonate.lng);
          }
        });

        const polyline = new window.H.map.Polyline(lineString, {
          style: {
            strokeColor: selectedRoute.color || '#004A7C',
            lineWidth: 4,
            lineDash: [4, 4]
          }
        });

        group.addObject(polyline);
        console.log('Using fallback simple polyline');
      }
    }

    // Add padding to bounds for better view
    const latDiff = bounds.getTop() - bounds.getBottom();
    const lngDiff = bounds.getRight() - bounds.getLeft();
    const paddingFactor = 0.2; // 20% padding

    const paddedBounds = new window.H.geo.Rect(
      bounds.getTop() + (latDiff * paddingFactor),
      bounds.getLeft() - (lngDiff * paddingFactor),
      bounds.getBottom() - (latDiff * paddingFactor),
      bounds.getRight() + (lngDiff * paddingFactor)
    );

    // Fit view to show all markers with padding
    map.getViewModel().setLookAtData({
      bounds: paddedBounds
    });

    console.log('Map initialized successfully');
  };

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const data = await routesAPI.getAllRoutes();
      console.log('Loaded routes:', data);
      setRoutes(data);
      if (data.length > 0) {
        selectRoute(data[0]);
      }
    } catch (error) {
      console.error('Error loading routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectRoute = async (route) => {
    try {
      const fullRoute = await routesAPI.getRouteById(route.id);
      setSelectedRoute(fullRoute || route);
    } catch (error) {
      console.error('Error loading route detail:', error);
      setSelectedRoute(route);
    }
  };

  const filteredRoutes = routes.filter(route => {
    const matchesSearch = (route.nume || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (route.numar || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (route.punctPlecare || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (route.punctSosire || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterType === 'all' || route.tip === filterType;

    return matchesSearch && matchesFilter;
  });

  // Get first and last trip times from curseProgram
  const getScheduleTimes = (route) => {
    if (!route.curseProgram || route.curseProgram.length === 0) {
      return { first: '05:00', last: '23:00' };
    }

    const sortedTrips = [...route.curseProgram].sort((a, b) =>
      (a.oraplecare || '').localeCompare(b.oraplecare || '')
    );

    return {
      first: sortedTrips[0]?.oraplecare || '05:00',
      last: sortedTrips[sortedTrips.length - 1]?.oraplecare || '23:00'
    };
  };

  return (
    <div className="routes-page">
      {/* Hero Section */}
      <section className="routes-hero">
        <div className="container">
          <h1 className="page-title">Rute & Curse</h1>
          <p className="page-subtitle">
            Explorează toate rutele noastre și găsește cea mai bună cale pentru călătoria ta
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="routes-controls section">
        <div className="container">
          <div className="controls-grid">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Caută după linie, origine sau destinație..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-box">
              <Filter size={20} />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select"
              >
                <option value="all">Toate Rutele</option>
                <option value="urban">Urban</option>
                <option value="judetean">Județean</option>
                <option value="interjudetean">Interjudețean</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Routes Content */}
      <section className="routes-content section">
        <div className="container-wide">
          {/* Routes List - Horizontal */}
          <div className="routes-horizontal-container">
            <h3 className="routes-horizontal-title">
              <Bus size={20} />
              Selectează Ruta
            </h3>

            {loading ? (
              <div className="loading-state">Se încarcă rutele...</div>
            ) : filteredRoutes.length === 0 ? (
              <div className="empty-state">
                <Info size={48} />
                <p>Nu s-au găsit rute care să corespundă criteriilor</p>
              </div>
            ) : (
              <div className="routes-horizontal-list">
                {filteredRoutes.map((route) => (
                  <div
                    key={route.id}
                    className={`route-card-horizontal ${selectedRoute?.id === route.id ? 'route-card-active' : ''}`}
                    onClick={() => selectRoute(route)}
                  >
                    <div
                      className="route-badge-h"
                      style={{ backgroundColor: route.color || '#004A7C' }}
                    >
                      {route.numar}
                    </div>
                    <div className="route-info-h">
                      <h4 className="route-name-h">{route.nume}</h4>
                      <div className="route-meta-h">
                        <span className="route-type-h">{getRouteTypeLabel(route.tip)}</span>
                        <span className="route-trips-h">{route.curseProgram?.length || 0} curse</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Route Details - Full Width */}
          <div className="routes-full-width">
              {selectedRoute ? (
                <>
                  {/* Route Header */}
                  <div className="route-details-header card">
                    <div className="route-header-top">
                      <div
                        className="route-badge-large"
                        style={{ backgroundColor: selectedRoute.color || '#004A7C' }}
                      >
                        {selectedRoute.numar}
                      </div>
                      <div className="route-header-info">
                        <h2>{selectedRoute.nume}</h2>
                        <span className="route-type-badge">
                          {getRouteTypeLabel(selectedRoute.tip)}
                        </span>
                      </div>
                    </div>

                    <div className="route-stats-grid">
                      <div className="route-stat">
                        <MapPin size={20} />
                        <div>
                          <span className="route-stat-label">Distanță</span>
                          <span className="route-stat-value">{selectedRoute.distanta || 0} km</span>
                        </div>
                      </div>
                      <div className="route-stat">
                        <Clock size={20} />
                        <div>
                          <span className="route-stat-label">Durată</span>
                          <span className="route-stat-value">~{selectedRoute.durataMedie || 0} min</span>
                        </div>
                      </div>
                      <div className="route-stat">
                        <Bus size={20} />
                        <div>
                          <span className="route-stat-label">Curse</span>
                          <span className="route-stat-value">{selectedRoute.curseProgram?.length || 0}/zi</span>
                        </div>
                      </div>
                    </div>

                 

                    {/* Stations List - Horizontal Timeline */}
                    {selectedRoute.variante && selectedRoute.variante.length > 0 && (() => {
                      const varianta = selectedRoute.variante.find(v => v.directie === selectedDirection) || selectedRoute.variante[0];
                      const statii = varianta?.statii || [];
                      return (
                      <div className="route-stations">
                        <div className="route-stations-header">
                          <h4>Stații pe Traseu ({statii.length})</h4>
                          <div className="direction-toggle">
                            {selectedRoute.variante.map(v => (
                              <button
                                key={v.directie}
                                className={`direction-btn ${selectedDirection === v.directie ? 'active' : ''}`}
                                onClick={() => setSelectedDirection(v.directie)}
                              >
                                {v.directie === 'tur' ? '→ Tur' : '← Retur'}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="stations-timeline-horizontal">
                          {statii
                            .sort((a, b) => (a.ordine || 0) - (b.ordine || 0))
                            .map((statie, index) => (
                              <div key={statie.id || index} className="station-timeline-item">
                                <div className="station-dot-wrapper">
                                  <div className={`station-dot-h ${index === 0 ? 'first' : index === statii.length - 1 ? 'last' : 'middle'}`}>
                                    {index + 1}
                                  </div>
                                  {index < statii.length - 1 && (
                                    <div className="station-line-h"></div>
                                  )}
                                </div>
                                <div className="station-label">
                                  <span className="station-name-h">{statie.nume}</span>
                                  <div className="station-meta-h">
                                    {statie.km > 0 && <span className="station-km-h">{statie.km.toFixed(1)} km</span>}
                                    {statie.pret > 0 && <span className="station-price-h">{statie.pret.toFixed(2)} RON</span>}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                  </div>

                  {/* Map Container */}
                  <div className="route-map-container card">
                    <h3 className="map-title">
                      <MapPin size={24} />
                      Hartă Traseu - Linia {selectedRoute.numar}
                    </h3>
                    <div ref={mapRef} className="google-map"></div>
                    <div className="map-legend">
                      <div className="legend-item">
                        <div className="legend-dot origin"></div>
                        <span>Stație de plecare</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-dot intermediate"></div>
                        <span>Stație intermediară</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-dot destination"></div>
                        <span>Stație finală</span>
                      </div>
                    </div>
                  </div>

                  {/* Schedule Section */}
                  <div className="route-schedule card">
                    <h3 className="schedule-title">
                      <Clock size={24} />
                      Program de Circulație ({selectedRoute.curseProgram?.length || 0} curse)
                    </h3>

                    {selectedRoute.curseProgram && selectedRoute.curseProgram.length > 0 ? (
                      <div className="schedule-trips">
                        <div className="schedule-times">
                          {(() => {
                            const times = getScheduleTimes(selectedRoute);
                            return (
                              <>
                                <span><strong>Prima cursă:</strong> {times.first}</span>
                                <span><strong>Ultima cursă:</strong> {times.last}</span>
                              </>
                            );
                          })()}
                        </div>
                        <div className="trips-list">
                          {selectedRoute.curseProgram
                            .sort((a, b) => (a.oraplecare || '').localeCompare(b.oraplecare || ''))
                            .map((cursa, index) => (
                              <div key={cursa.id || index} className="trip-card">
                                <div
                                  className="trip-header"
                                  onClick={() => setExpandedTrip(expandedTrip === cursa.id ? null : cursa.id)}
                                >
                                  <div className="trip-header-left">
                                    <div className="trip-time">{cursa.oraplecare}</div>
                                    <div className="trip-route-info">
                                      <div className="trip-details">
                                        <span className="trip-direction">
                                          {cursa.directie === 'tur' ? '→' : '←'} {cursa.directie === 'tur' ? 'Tur' : 'Retur'}
                                        </span>
                                        <span className="trip-type">
                                          {cursa.tipCursa === 'completa' ? 'Cursă Completă' : 'Varianta'}
                                        </span>
                                      </div>
                                      {cursa.statii_program && cursa.statii_program.length > 0 && (
                                        <div className="trip-endpoints">
                                          <span className="trip-start">
                                            {cursa.statii_program[0].nume}
                                          </span>
                                          <span className="trip-arrow">→</span>
                                          <span className="trip-end">
                                            {cursa.statii_program[cursa.statii_program.length - 1].nume}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <ChevronRight
                                    size={20}
                                    className={`trip-expand-icon ${expandedTrip === cursa.id ? 'expanded' : ''}`}
                                  />
                                </div>

                                {expandedTrip === cursa.id && cursa.statii_program && (
                                  <div className="trip-stations">
                                    <h5>Stații pe traseu ({cursa.statii_program.length})</h5>
                                    <div className="trip-stations-list">
                                      {cursa.statii_program.map((statie, idx) => (
                                        <div key={idx} className="trip-station-item">
                                          <div className="station-timeline">
                                            <div className={`station-dot ${statie.tip || ''}`}></div>
                                            {idx < cursa.statii_program.length - 1 && (
                                              <div className="station-line"></div>
                                            )}
                                          </div>
                                          <div className="station-content">
                                            <div className="station-time">{statie.ora}</div>
                                            <div className="station-details">
                                              <span className="station-name">{statie.nume}</span>
                                              <div className="station-meta">
                                                {statie.km > 0 && (
                                                  <span className="station-distance">{statie.km.toFixed(1)} km</span>
                                                )}
                                                {statie.pret > 0 && (
                                                  <span className="station-price">{statie.pret.toFixed(2)} RON</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    ) : (
                      <p className="no-schedule">Nu există curse programate pentru această rută</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="no-route-selected card">
                  <Navigation size={64} />
                  <h3>Selectează o rută</h3>
                  <p>Alege o rută din lista de mai sus pentru a vedea detaliile</p>
                </div>
              )}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="section quick-actions-section">
        <div className="container">
          <div className="quick-actions-grid">
            <div className="quick-action-card card">
              <MapPin size={32} />
              <h3>Planifică Călătoria</h3>
              <p>Folosește planificatorul nostru pentru cea mai bună rută</p>
              <button className="btn btn-secondary">Planifică Acum</button>
            </div>
            <div className="quick-action-card card">
              <Clock size={32} />
              <h3>Urmărire Live</h3>
              <p>Vezi în timp real unde se află autobuzul tău</p>
              <button className="btn btn-secondary">Vezi Live</button>
            </div>
            <div className="quick-action-card card">
              <Bus size={32} />
              <h3>Descarcă Orare</h3>
              <p>Descarcă orarele complete pentru toate liniile</p>
              <button className="btn btn-secondary">Descarcă PDF</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

function getRouteTypeLabel(type) {
  const types = {
    urban: 'Urban',
    judetean: 'Județean',
    interjudetean: 'Interjudețean',
  };
  return types[type] || type;
}

export default Routes;
